import { useRef } from "react";
import { motion, useScroll, useTransform, useSpring, useMotionValue } from "framer-motion";
import { ArrowRight, Layers, Play, Palette } from "lucide-react";
import { MaskedLine } from "./Reveal";
import { scrollToId } from "@/lib/site";

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const sx = useSpring(mx, { stiffness: 60, damping: 18 });
  const sy = useSpring(my, { stiffness: 60, damping: 18 });
  const chip1x = useTransform(sx, (v) => v * 22);
  const chip1y = useTransform(sy, (v) => v * 16);
  const chip2x = useTransform(sx, (v) => v * -16);
  const chip2y = useTransform(sy, (v) => v * -12);
  const chip3x = useTransform(sx, (v) => v * 10);
  const chip3y = useTransform(sy, (v) => v * 22);

  const onMouseMove = (e) => {
    const r = ref.current?.getBoundingClientRect();
    if (!r) return;
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };

  return (
    <section
      id="home"
      ref={ref}
      onMouseMove={onMouseMove}
      className="relative min-h-[100svh] flex items-center overflow-hidden"
      data-testid="hero-section"
    >
      {/* Parallax background */}
      <motion.div style={{ y: bgY }} className="absolute inset-0 -z-10 scale-110" data-testid="hero-background">
        <img
          src="https://images.unsplash.com/photo-1627244714766-94dab62ed964?crop=entropy&cs=srgb&fm=jpg&q=85"
          alt="Creative video editing workstation in a dark studio"
          className="h-full w-full object-cover opacity-45"
          fetchpriority="high"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-obsidian via-obsidian/90 to-obsidian/30" />
        <div className="absolute inset-0 bg-gradient-to-t from-obsidian via-transparent to-obsidian/70" />
        <div className="absolute inset-0 grid-lines [mask-image:radial-gradient(ellipse_at_center,black_20%,transparent_75%)]" />
      </motion.div>

      {/* Amber ambient glow */}
      <div className="absolute -left-40 top-1/3 -z-10 h-[480px] w-[480px] rounded-full bg-amber-500/10 blur-[140px]" />

      <motion.div style={{ opacity: fade }} className="relative w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-28 pb-20">
        <div className="grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <motion.div
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.15 }}
              className="inline-flex items-center gap-2.5 rounded-full glass px-4 py-2"
              data-testid="hero-eyebrow-badge"
            >
              <span className="relative flex h-2 w-2">
                <span className="absolute inline-flex h-full w-full rounded-full bg-amber-500 opacity-75 animate-ping" />
                <span className="relative inline-flex h-2 w-2 rounded-full bg-amber-500" />
              </span>
              <span className="font-mono text-[10px] sm:text-xs uppercase tracking-[0.24em] text-gray-300">
                Motion Mind Institute · Kaushambi
              </span>
            </motion.div>

            <h1 className="mt-7 font-display font-extrabold tracking-tight leading-[1.06] text-[2.15rem] sm:text-5xl lg:text-6xl xl:text-[4.35rem] text-white">
              <MaskedLine delay={0.3}>Turn Your</MaskedLine>
              <MaskedLine delay={0.44}>
                <span className="text-brand">Creativity</span>
              </MaskedLine>
              <MaskedLine delay={0.58}>Into a Career.</MaskedLine>
            </h1>

            <motion.p
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.95 }}
              className="mt-6 max-w-xl text-base sm:text-lg text-gray-300/90 leading-relaxed"
              data-testid="hero-supporting-text"
            >
              Learn Graphic Design, Photoshop &amp; Video Editing with practical training designed to help you
              build real skills, real projects and a professional portfolio.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 1.1 }}
              className="mt-9 flex flex-wrap items-center gap-4"
            >
              <button
                onClick={() => scrollToId("#contact")}
                className="group inline-flex items-center gap-2 rounded-full bg-amber-500 px-7 py-4 text-sm sm:text-base font-semibold text-obsidian hover:bg-amber-400 hover:shadow-glow-amber hover:-translate-y-0.5 transition-all duration-300"
                data-testid="hero-enquire-button"
              >
                Enquire Now
                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
              </button>
              <button
                onClick={() => scrollToId("#courses")}
                className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/[0.03] px-7 py-4 text-sm sm:text-base font-medium text-white backdrop-blur hover:border-amber-500/60 hover:text-amber-400 hover:-translate-y-0.5 transition-all duration-300"
                data-testid="hero-explore-courses-button"
              >
                Explore Courses
              </button>
            </motion.div>

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 1, delay: 1.35 }}
              className="mt-10 flex flex-wrap gap-x-6 gap-y-2 font-mono text-[10px] sm:text-[11px] uppercase tracking-[0.22em] text-gray-500"
              data-testid="hero-trust-row"
            >
              <span>Practical Learning</span>
              <span className="text-amber-500/60">✦</span>
              <span>Portfolio Building</span>
              <span className="text-amber-500/60">✦</span>
              <span>Career-Focused Skills</span>
            </motion.div>
          </div>

          {/* Floating creative-tool chips (3D parallax moment) */}
          <div className="hidden lg:block lg:col-span-5 relative h-[420px]">
            <motion.div
              style={{ x: chip1x, y: chip1y }}
              className="absolute right-6 top-6 w-64 rounded-2xl glass p-4 shadow-2xl"
              data-testid="hero-chip-photoshop"
            >
              <div className="flex items-center gap-2 text-xs font-mono uppercase tracking-widest text-gray-400">
                <Layers className="h-4 w-4 text-amber-500" /> Layers Panel
              </div>
              <div className="mt-3 space-y-2">
                {["w-3/4", "w-1/2", "w-5/6", "w-2/3"].map((w, i) => (
                  <div key={i} className="h-2.5 rounded-full bg-white/[0.07] overflow-hidden">
                    <motion.div
                      className={`h-full rounded-full bg-gradient-to-r from-amber-500/70 to-brand/70 ${w}`}
                      initial={{ width: 0 }}
                      animate={{ width: "100%" }}
                      transition={{ duration: 1.2, delay: 1.4 + i * 0.15 }}
                      style={{ maxWidth: w === "w-3/4" ? "75%" : w === "w-1/2" ? "50%" : w === "w-5/6" ? "83%" : "66%" }}
                    />
                  </div>
                ))}
              </div>
            </motion.div>

            <motion.div
              style={{ x: chip2x, y: chip2y }}
              className="absolute left-0 top-40 w-72 rounded-2xl glass p-4 shadow-2xl animate-floaty"
              data-testid="hero-chip-timeline"
            >
              <div className="flex items-center gap-2 text-xs font-mono uppercase tracking-widest text-gray-400">
                <Play className="h-4 w-4 text-amber-500" /> Editing Timeline
              </div>
              <div className="mt-3 flex items-end gap-1.5 h-12">
                {[38, 62, 45, 80, 55, 92, 40, 68, 50, 74, 34, 58].map((h, i) => (
                  <motion.span
                    key={i}
                    className={`flex-1 rounded-sm ${i % 3 === 0 ? "bg-brand/80" : "bg-white/15"}`}
                    initial={{ height: 4 }}
                    animate={{ height: `${h}%` }}
                    transition={{ duration: 0.9, delay: 1.3 + i * 0.07, ease: [0.22, 1, 0.36, 1] }}
                  />
                ))}
              </div>
              <div className="mt-2 h-1 rounded-full shimmer-line animate-shimmer" />
            </motion.div>

            <motion.div
              style={{ x: chip3x, y: chip3y }}
              className="absolute right-0 bottom-4 w-60 rounded-2xl glass p-4 shadow-2xl"
              data-testid="hero-chip-design"
            >
              <div className="flex items-center gap-2 text-xs font-mono uppercase tracking-widest text-gray-400">
                <Palette className="h-4 w-4 text-amber-500" /> Color Theory
              </div>
              <div className="mt-3 flex items-center gap-3">
                <span className="h-9 w-9 rounded-full bg-brand shadow-glow-amber-sm" />
                <span className="h-9 w-9 rounded-full bg-white/85" />
                <span className="h-9 w-9 rounded-full bg-gray-600" />
                <span className="h-9 w-9 rounded-full border border-white/20" />
                <span className="font-display text-xl font-bold text-white/80">Aa</span>
              </div>
            </motion.div>
          </div>
        </div>
      </motion.div>

      {/* Scroll cue */}
      <motion.div
        style={{ opacity: fade }}
        className="absolute bottom-7 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2"
        data-testid="hero-scroll-cue"
      >
        <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-gray-500">Scroll</span>
        <span className="h-10 w-px overflow-hidden">
          <span className="block h-full w-px bg-gradient-to-b from-amber-500 to-transparent animate-scroll-line" />
        </span>
      </motion.div>
    </section>
  );
}
